import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('Auth loading timeout - setting loading to false')
        setLoading(false)
      }
    }, 5000)

    // Get initial session
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setUser(session?.user ?? null)
        if (session?.user) {
          fetchProfile(session.user.id)
        } else {
          setLoading(false)
        }
      })
      .catch((error) => {
        console.error('Error getting session:', error)
        setLoading(false)
      })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null)
        if (session?.user) {
          await fetchProfile(session.user.id)
        } else {
          setProfile(null)
          setLoading(false)
        }
      }
    )

    return () => {
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  async function fetchProfile(userId) {
    try {
      let { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      // If profile doesn't exist, create it
      if (error && error.code === 'PGRST116') {
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({ id: user.id, email: user.email })
            .select()
            .single()

          if (insertError) {
            console.error('Error creating profile:', insertError)
          } else {
            data = newProfile
          }
        }
      } else if (error) {
        throw error
      }

      setProfile(data)
    } catch (error) {
      console.error('Error fetching profile:', error)
    } finally {
      setLoading(false)
    }
  }

  async function updateProfile(updates) {
    console.log('AuthContext: updateProfile called, user:', user?.id)
    if (!user?.id) {
      console.error('No user ID for profile update')
      return { data: null, error: new Error('Not authenticated') }
    }

    try {
      console.log('AuthContext: Updating profile via Netlify function:', user.id, updates)

      const response = await fetch('/.netlify/functions/update-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, updates })
      })

      const result = await response.json()
      console.log('AuthContext: Update response:', result)

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update profile')
      }

      console.log('Profile updated:', result.data)
      setProfile(result.data)
      return { data: result.data, error: null }
    } catch (error) {
      console.error('Error updating profile:', error)
      return { data: null, error }
    }
  }

  async function signInWithMagicLink(email) {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    })
    return { error }
  }

  async function signOut() {
    console.log('AuthContext: signOut called')
    // Clear state and redirect immediately - don't wait for Supabase
    setUser(null)
    setProfile(null)

    // Try to sign out from Supabase in the background
    supabase.auth.signOut().catch(err => {
      console.error('Supabase signOut error:', err)
    })

    // Redirect immediately
    window.location.href = '/'
  }

  const value = {
    user,
    profile,
    loading,
    signInWithMagicLink,
    signOut,
    updateProfile,
    refreshProfile: () => user && fetchProfile(user.id)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
